import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Typography, Loader, Input } from '@lucid/lego';
import { useSearchBusinesses } from '../../api/services/lupp/order/order';
import { useStore } from '../../store/store';
import { getCountryCodeFromLocale } from '../../utils/tools';
import { isClickOutside, getValidationError } from './utils';
import type { BusinessNameSearchProps } from './types';
import type { BusinessSearchResult } from '../../api/services/lupp/order/dto.order';
import styles from './BusinessNameSearch.module.scss';

/**
 * BusinessNameSearch Component
 *
 * A typeahead search component for finding businesses using D&B (Dun & Bradstreet) API.
 * Features:
 * - Debounced search (400ms)
 * - Keyboard navigation (arrows, enter, escape)
 * - Loading, error, and empty states
 * - Accessibility (ARIA labels, roles)
 * - Required field validation
 *
 * @example
 * <BusinessNameSearch
 *   name="companyName"
 *   label="Company Name"
 *   required
 *   onChange={(business) => console.log(business)}
 * />
 */
export const BusinessNameSearch: React.FC<BusinessNameSearchProps> = ({
  name,
  label,
  placeholder = 'Search for company...',
  required = false,
  disabled = false,
  defaultValue = null,
  onChange,
  onValidationError,
  theme = 'light',
  dataTestId = 'business-name-search',
  helperText,
  error: externalError,
  messages,
}) => {
  // State management
  const [inputValue, setInputValue] = useState(defaultValue?.name || '');
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSearchResult | null>(
    defaultValue
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get country from locale
  const locale = useStore((state) => state.locale);
  const country = getCountryCodeFromLocale(locale);

  // Debounced search term update
  const debouncedSetSearchTerm = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (value: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setDebouncedSearchTerm(value), 400);
    };
  }, []);

  const {
    searchingText = 'Searching...',
    apiErrorText = 'Search is temporarily unavailable. Please try again later.',
    minCharsText = 'Type at least 3 characters to search',
    noResultsText = 'No matching results found.',
    validationRequiredText = 'Please select a valid Company from the list.',
  } = messages ?? {};

  // API call - only when we have a search term and no selection
  const {
    data: results = [],
    isLoading,
    error: apiError,
  } = useSearchBusinesses(
    debouncedSearchTerm,
    country,
    !selectedBusiness && debouncedSearchTerm.length >= 3
  );

  // Validation error
  const validationError = useMemo(
    () => getValidationError(required, !!selectedBusiness, hasBlurred, validationRequiredText),
    [required, selectedBusiness, hasBlurred, validationRequiredText]
  );

  // Display error (external or validation or API)
  const displayError = externalError || validationError || (apiError ? apiErrorText : null);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Clear selection if user modifies input
    if (selectedBusiness) {
      setSelectedBusiness(null);
      onChange(null);
    }

    // Update debounced search term
    debouncedSetSearchTerm(value);

    // Open dropdown if we have enough characters
    if (value.length >= 3) {
      setIsDropdownOpen(true);
      setHighlightedIndex(-1);
    } else {
      setIsDropdownOpen(false);
    }
  };

  // Handle selection
  const handleSelect = useCallback(
    (business: BusinessSearchResult) => {
      setSelectedBusiness(business);
      setInputValue(business.name);
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      onChange(business);
    },
    [onChange]
  );

  // Handle clear
  const handleClear = useCallback(() => {
    setSelectedBusiness(null);
    setInputValue('');
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    setHasBlurred(false);
    setDebouncedSearchTerm('');
    onChange(null);
    inputRef.current?.focus();
  }, [onChange]);

  // Handle blur for validation
  const handleBlur = () => {
    setHasBlurred(true);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(results.length - 1);
        break;
      case 'Tab':
        setIsDropdownOpen(false);
        break;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isClickOutside(containerRef.current, event)) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isDropdownOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  // Notify validation errors
  useEffect(() => {
    if (onValidationError) {
      onValidationError(validationError);
    }
  }, [validationError, onValidationError]);

  // Determine what to show in dropdown
  const dropdownContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className={styles.dropdownMessage}>
          <Loader size={24} theme={theme} />
          <Typography variant="body" size="small" theme={theme} color="gray-5">
            {searchingText}
          </Typography>
        </div>
      );
    }

    if (apiError) {
      return (
        <div className={styles.dropdownMessage}>
          <Typography variant="body" size="small" theme={theme} color="error-1">
            {apiErrorText}
          </Typography>
        </div>
      );
    }

    if (debouncedSearchTerm.length < 3) {
      return (
        <div className={styles.dropdownMessage}>
          <Typography variant="body" size="small" theme={theme} color="gray-5">
            {minCharsText}
          </Typography>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className={styles.dropdownMessage}>
          <Typography variant="body" size="small" theme={theme} color="gray-5">
            {noResultsText}
          </Typography>
        </div>
      );
    }

    return results.map((result, index) => {
      const isHighlighted = index === highlightedIndex;
      const isSelected = selectedBusiness?.duns === result.duns;

      return (
        <div
          key={result.duns}
          className={styles.resultItem}
          data-highlighted={isHighlighted}
          onClick={() => handleSelect(result)}
          onMouseEnter={() => setHighlightedIndex(index)}
          role="option"
          aria-selected={isSelected}
          data-testid={`${dataTestId}-result-${index}`}
        >
          <Typography
            className={styles.resultName}
            variant="body"
            size="medium"
            theme={theme}
            color="white"
          >
            {result.name}
          </Typography>
          <Typography
            className={styles.resultLocation}
            variant="body"
            size="extraSmall"
            theme={theme}
            color="gray-5"
          >
            {result.displayText.split(' - ')[1] || result.city}
          </Typography>
        </div>
      );
    });
  }, [
    isLoading,
    apiError,
    debouncedSearchTerm,
    results,
    highlightedIndex,
    selectedBusiness,
    handleSelect,
    dataTestId,
    theme,
    searchingText,
    apiErrorText,
    minCharsText,
    noResultsText,
  ]);

  // Container classes
  const containerClasses = classNames(styles.container, {
    [styles.disabled]: disabled,
    [styles[`theme${theme.charAt(0).toUpperCase() + theme.slice(1)}`]]: theme,
  });

  const helperIcon = selectedBusiness
    ? { category: 'global', name: 'close', dataTestId: `${dataTestId}-clear-button` }
    : { category: 'arrows', name: 'chevron-down' };

  return (
    <div ref={containerRef} className={containerClasses} data-testid={dataTestId}>
      <div className={styles.inputContainer}>
        <Input
          ref={inputRef}
          name={name}
          label={label}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          helperText={helperText}
          theme={theme}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          validationResponse={
            displayError ? { validationType: 'error', message: displayError } : undefined
          }
          helperIcon={helperIcon}
          handleIconClick={() => {
            if (disabled) return;
            if (selectedBusiness) {
              handleClear();
              return;
            }
            setIsDropdownOpen((prev) => !prev);
          }}
          dataTestId={`${dataTestId}-input`}
          inputProps={{
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': isDropdownOpen,
            'aria-controls': `${name}-dropdown`,
            'aria-activedescendant':
              highlightedIndex >= 0 ? `${name}-option-${highlightedIndex}` : undefined,
            'aria-invalid': !!displayError,
            'aria-required': required,
            onKeyDown: handleKeyDown,
            onFocus: () => {
              if (inputValue.length >= 3 && !selectedBusiness) {
                setIsDropdownOpen(true);
              }
            },
          }}
        />
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          id={`${name}-dropdown`}
          className={styles.dropdown}
          role="listbox"
          aria-label={`${label} search results`}
          data-testid={`${dataTestId}-dropdown`}
        >
          {dropdownContent}
        </div>
      )}
    </div>
  );
};

export default BusinessNameSearch;
